import type { UsersMeResponse } from '@ionq/trpc-console';
import { redirectDocument } from 'react-router';
import { getSignInURL, signOutRedirect } from './session';
import {
    isTRPCAuthError,
    isTRPCForbiddenError,
    queryClient,
    sessionUserQueryOptions,
} from './trpc';

/** users.me for the root loader. Throws the document redirect on session
 * errors; lives outside the component graph so the error contract is
 * unit-testable (no assets, no LaunchDarkly). */
export async function fetchSessionUser(
    request: Request,
): Promise<UsersMeResponse> {
    try {
        return await queryClient.fetchQuery(sessionUserQueryOptions());
    } catch (error) {
        // request.url, not window.location — loaders run for the *pending*
        // navigation.
        const url = new URL(request.url);
        const targetUri = `${url.pathname}${url.search}${url.hash}`;
        if (isTRPCAuthError(error)) {
            throw redirectDocument(await signOutRedirect({ targetUri }));
        }
        if (isTRPCForbiddenError(error)) {
            // Signed in but no org-scoped account (e.g. enterprise-only
            // user). Never revoke: other apps ride the session. The 403
            // banner only right after a sign-in; any other visit gets the
            // plain form. Both statuses hold the SSO's auto-bounce, so
            // neither can loop.
            throw redirectDocument(
                cameFromSignIn()
                    ? getSignInURL({ status: 'console-forbidden' })
                    : getSignInURL({ status: 'console-reauth', targetUri }),
            );
        }
        throw error;
    }
}

/** The document was navigated here from this origin's /auth/* mount. */
function cameFromSignIn(): boolean {
    try {
        const referrer = new URL(document.referrer);
        return (
            referrer.origin === window.location.origin &&
            referrer.pathname.startsWith('/auth/')
        );
    } catch {
        return false;
    }
}
